import React from "react";
import { Link } from "react-router";
import { Tooltip } from "@/components/common/Tooltip";

import { Notification, NotificationTypeJS } from "target_wasm";
import { formatBytes } from "components/canvas/detailsSide/utils/formatBytes";
import { formatDayMonthYear } from "@/lib/dates";

interface NotificationMessageProps {
  notification: Notification;
  onResourceClick: (
    resourceType: "model" | "table" | "org" | "space",
    resourceId: string,
    tableId?: string,
  ) => void;
}

export const NotificationMessage: React.FC<NotificationMessageProps> = ({
  notification,
  onResourceClick,
}) => {
  const notificationType = notification.notification_type_js();
  const notificationData = notification.get_data_js();
  const emailDetails = notificationData.in_email_details;
  const fromUserName = emailDetails?.from_client_name || "Someone";
  const fromUserEmail = emailDetails?.from_email as string | undefined;

  const ResourceLink: React.FC<{
    children: React.ReactNode;
    resourceType: "model" | "table" | "org" | "space";
    resourceId: string;
    tooltip?: string;
    tableId?: string;
  }> = ({ children, resourceType, resourceId, tooltip, tableId }) => (
    <Tooltip title={tooltip}>
      <span
        className="text-fg-muted-semibold hover:text-fg-semibold cursor-pointer transition-colors duration-150"
        onClick={(e) => {
          e.stopPropagation();
          onResourceClick(resourceType, resourceId, tableId);
        }}
      >
        {children}
      </span>
    </Tooltip>
  );

  const UserName: React.FC<{ children: React.ReactNode }> = ({ children }) =>
    fromUserEmail ? (
      <Tooltip title={fromUserEmail}>
        <span className="text-fg-medium cursor-pointer">{children}</span>
      </Tooltip>
    ) : (
      <span className="text-fg-medium">{children}</span>
    );

  const TableLink: React.FC<{
    modelId?: string;
    tableId?: string;
    tableName: string;
    tooltip?: string;
  }> = ({ modelId, tableId, tableName, tooltip }) =>
    modelId ? (
      <ResourceLink
        resourceType="table"
        resourceId={modelId}
        tableId={tableId}
        tooltip={tooltip ?? `View ${tableName}`}
      >
        <span className="font-medium">{tableName}</span>
      </ResourceLink>
    ) : (
      <span className="font-medium">{tableName}</span>
    );

  switch (notificationType) {
    case NotificationTypeJS.OrgInvite:
      return (
        <>
          <UserName>{fromUserName}</UserName> invited you to join{" "}
          <span className="font-medium">
            {notificationData.org_name || "an organization"}
          </span>
        </>
      );

    case NotificationTypeJS.OrgAccessRevoked:
      return (
        <>
          <UserName>{fromUserName}</UserName> removed you from{" "}
          <span className="font-medium">
            {notificationData.org_name || "an organization"}
          </span>
        </>
      );

    case NotificationTypeJS.SpaceAccess: {
      const verb =
        {
          granted: `gave you ${notificationData.role ? `${notificationData.role} ` : ""}access to`,
          revoked: "removed your access to",
          deleted: "deleted the space",
        }[notificationData.change as string] ??
        (notificationData.classification === "sealed" ? "sealed" : "un-sealed");
      return (
        <>
          <UserName>{fromUserName}</UserName> {verb}{" "}
          <span className="font-medium">
            {notificationData.space_name || "a space"}
          </span>
          {notificationData.change === "granted" &&
          notificationData.org_name ? (
            <>
              {" "}
              in{" "}
              <span className="font-medium">{notificationData.org_name}</span>
            </>
          ) : null}
        </>
      );
    }

    case NotificationTypeJS.ModelShared:
      return (
        <>
          <UserName>{fromUserName}</UserName> shared model{" "}
          <ResourceLink
            resourceType="model"
            resourceId={notificationData.model_details?.model_id}
            tooltip={`Open ${notificationData.model_details?.model_name || "Unnamed Model"}`}
          >
            {notificationData.model_details?.model_name || "Unnamed Model"}
          </ResourceLink>{" "}
          with you
        </>
      );

    case NotificationTypeJS.ModelDeleted:
      return (
        <>
          Model{" "}
          <span>
            {notificationData.model_details?.model_name || "Unnamed Model"}
          </span>{" "}
          was deleted
        </>
      );

    case NotificationTypeJS.AccessRevokedFromModel:
      return (
        <>
          Access revoked from model{" "}
          <span>
            {notificationData.model_details?.model_name || "Unnamed Model"}
          </span>
        </>
      );

    case NotificationTypeJS.TableShared:
      return (
        <>
          <UserName>{fromUserName}</UserName> shared table{" "}
          <span>
            {notificationData.table_details?.table_name || "Unnamed Table"}{" "}
          </span>
          with you
        </>
      );

    case NotificationTypeJS.TableRequest:
      return (
        <>
          <UserName>{fromUserName}</UserName> requested{" "}
          {notificationData.values_only === true
            ? "access to the data of table"
            : "access to table"}{" "}
          <ResourceLink
            resourceType="table"
            resourceId={notificationData.parent_model}
            tableId={notificationData.table_details?.table_id}
            tooltip={`View ${notificationData.table_details?.table_name || "Unknown"}`}
          >
            {notificationData.table_details?.table_name || "Unknown"}
          </ResourceLink>
        </>
      );

    case NotificationTypeJS.AccessRevokedFromTable:
      return (
        <>
          Access revoked from table{" "}
          <span>{notificationData.table_details?.table_name || "Unknown"}</span>
        </>
      );

    case NotificationTypeJS.TableOwnershipTransferRequest: {
      const transferTableName =
        notificationData.table_details?.table_name || "Unknown Table";
      const transferSize = notificationData.table_size_bytes;
      const sizeStr =
        transferSize != null ? ` (${formatBytes(transferSize)})` : "";
      const currentUserId = localStorage.getItem("clientID") ?? "";
      const isSender = emailDetails?.from_client_id === currentUserId;
      const recipientName = emailDetails?.to_client_name || "someone";

      const transferModelId = notificationData.model_details?.model_id;
      const transferTableLink = (
        <TableLink
          modelId={transferModelId}
          tableId={notificationData.table_details?.table_id}
          tableName={transferTableName}
        />
      );

      if (isSender) {
        return (
          <>
            You sent a transfer offer for table {transferTableLink} to{" "}
            <UserName>{recipientName}</UserName>
          </>
        );
      }
      return (
        <>
          <UserName>{fromUserName}</UserName> wants to transfer table{" "}
          <strong>{transferTableName}</strong>
          {sizeStr} to you
        </>
      );
    }

    case NotificationTypeJS.TableOwnershipTransferResponse: {
      const responseStatus = notificationData.status?.toString().toLowerCase();
      const responseTableName =
        notificationData.table_details?.table_name || "Unknown Table";
      const responseTableLink = (
        <TableLink
          modelId={notificationData.model_details?.model_id}
          tableId={notificationData.table_details?.table_id}
          tableName={responseTableName}
        />
      );
      return (
        <>
          <UserName>{fromUserName}</UserName> {responseStatus} your transfer of{" "}
          {responseTableLink}
        </>
      );
    }

    case NotificationTypeJS.TableAccessResponse: {
      const status = notificationData.request_status?.toString().toLowerCase();
      const tableName = notificationData.table_details?.table_name;
      const targetModelId = notificationData.target_model;
      const isAccepted = status === "accepted" && !!targetModelId;
      const displayStatus = status === "rejected" ? "declined" : status;
      const tableId = notificationData.table_details?.table_id;

      return (
        <>
          <UserName>{fromUserName}</UserName> {displayStatus} your access
          request to{" "}
          {isAccepted ? (
            <ResourceLink
              resourceType="table"
              resourceId={targetModelId}
              tableId={tableId}
              tooltip={`Open in Model`}
            >
              {tableName}
            </ResourceLink>
          ) : (
            <span>{tableName}</span>
          )}
        </>
      );
    }

    case NotificationTypeJS.TableScheduledForDeletion: {
      const tableName =
        notificationData.table_details?.table_name || "Unknown Table";
      const tableId = notificationData.table_details?.table_id;
      const importingModelIds: string[] =
        notificationData.importing_model_ids ?? [];
      const importingModelNames: string[] =
        notificationData.importing_model_names ?? [];
      const deletionStatus = (
        notificationData.status as string | undefined
      )?.toLowerCase();
      const isDeleted = deletionStatus === "deleted";
      // A card sent before names were written links its table to the first model.
      const named = importingModelNames.length === importingModelIds.length;
      const firstModelId = named ? undefined : importingModelIds[0];
      return (
        <>
          Table{" "}
          {firstModelId && !isDeleted ? (
            <ResourceLink
              resourceType="model"
              resourceId={firstModelId}
              tableId={tableId}
              tooltip={`See ${tableName} in Model`}
            >
              <span className="font-medium">{tableName}</span>
            </ResourceLink>
          ) : (
            <span className="font-medium">{tableName}</span>
          )}{" "}
          {isDeleted ? "has been deleted" : "is scheduled for deletion"}
          {!isDeleted && named && importingModelIds.length > 0 && (
            <>
              . Imported in{" "}
              {importingModelIds.map((modelId, i) => (
                <React.Fragment key={modelId}>
                  {i > 0 && " · "}
                  <ResourceLink
                    resourceType="model"
                    resourceId={modelId}
                    tableId={tableId}
                    tooltip={`Open ${tableName} there`}
                  >
                    <span className="font-medium">
                      {importingModelNames[i] || "Untitled model"}
                    </span>
                  </ResourceLink>
                </React.Fragment>
              ))}
            </>
          )}
          {!named && importingModelIds.length > 1 && (
            <span className="text-fg-muted-regular">
              {" "}
              (imported in {importingModelIds.length} of your models)
            </span>
          )}
        </>
      );
    }

    case NotificationTypeJS.OrgScheduledForDeletion: {
      const purgeAt = notificationData.purge_at as number | undefined;
      const cancelled =
        (notificationData.status as string | undefined)?.toLowerCase() ===
        "cancelled";
      const orgName = (
        <span className="font-medium">
          {notificationData.org_notif?.org_name}
        </span>
      );
      // The card that announced the window is the card that retracts it, so
      // the copy has to stop telling people to take a copy.
      return cancelled ? (
        <>
          {orgName} was scheduled for deletion. That has been cancelled —
          nothing is going away.
        </>
      ) : (
        <>
          {orgName} is scheduled for deletion
          {purgeAt
            ? ` on ${formatDayMonthYear(new Date(purgeAt * 1000).toISOString())}`
            : ""}
          . Everything in it is in the Trash until then; an owner or admin can
          cancel, and all of it comes back.
        </>
      );
    }

    case NotificationTypeJS.OrgOwnerGranted:
      return (
        <>
          <UserName>{fromUserName}</UserName> made you an owner of{" "}
          <span className="font-medium">
            {notificationData.org_notif?.org_name}
          </span>
        </>
      );

    case NotificationTypeJS.SpaceScheduledForDeletion: {
      const purgeAt = notificationData.purge_at as number | undefined;
      const cancelled =
        (notificationData.status as string | undefined)?.toLowerCase() ===
        "cancelled";
      const where = (
        <>
          <span className="font-medium">{notificationData.space_name}</span> in{" "}
          <span className="font-medium">
            {notificationData.org_notif?.org_name}
          </span>
        </>
      );
      return cancelled ? (
        <>
          The space {where} was scheduled for deletion. That has been cancelled
          — nothing is going away.
        </>
      ) : (
        <>
          The space {where} is scheduled for deletion
          {purgeAt
            ? ` on ${formatDayMonthYear(new Date(purgeAt * 1000).toISOString())}`
            : ""}
          . Its sub-spaces and models are in the Trash until then — a manager
          can restore the space, and all of it comes back.
        </>
      );
    }

    case NotificationTypeJS.AppQuestion:
    case NotificationTypeJS.AppPaused: {
      const appId = notificationData.app_id as string | undefined;
      return (
        <>
          The app{" "}
          {appId ? (
            <Link
              to={`/apps/${appId}`}
              className="text-fg-muted-semibold hover:text-fg-semibold transition-colors duration-150"
              onClick={(e) => e.stopPropagation()}
            >
              {notificationData.app_name}
            </Link>
          ) : (
            <span className="font-medium">{notificationData.app_name}</span>
          )}{" "}
          {notificationType === NotificationTypeJS.AppQuestion ? "asks: " : ""}
          {notificationData.detail}
        </>
      );
    }

    case NotificationTypeJS.SpaceManagerNeeded: {
      const spaceId = notificationData.space_id as string | undefined;
      return (
        <>
          The space{" "}
          {spaceId ? (
            <ResourceLink
              resourceType="space"
              resourceId={spaceId}
              tooltip="Open space settings"
            >
              {notificationData.space_name}
            </ResourceLink>
          ) : (
            <span className="font-medium">{notificationData.space_name}</span>
          )}{" "}
          in{" "}
          <span className="font-medium">
            {notificationData.org_notif?.org_name}
          </span>{" "}
          has no manager. Nobody owns the models inside it until you appoint
          someone.
        </>
      );
    }

    case NotificationTypeJS.OrgSoleOwner: {
      const orgId = notificationData.org_notif?.org_id as string | undefined;
      const orgName = notificationData.org_notif?.org_name;
      return (
        <>
          You are the only owner of{" "}
          {orgId ? (
            <ResourceLink
              resourceType="org"
              resourceId={orgId}
              tooltip={`Manage members of ${orgName}`}
            >
              <span className="font-medium">{orgName}</span>
            </ResourceLink>
          ) : (
            <span className="font-medium">{orgName}</span>
          )}
          . Add a second owner — until then you can&apos;t step down or close
          your account.
        </>
      );
    }

    case NotificationTypeJS.OrgBillingPayerChanged: {
      const orgId = notificationData.org_notif?.org_id as string | undefined;
      const orgName = notificationData.org_notif?.org_name;
      const org = orgId ? (
        <ResourceLink
          resourceType="org"
          resourceId={orgId}
          tooltip={`Open ${orgName} settings`}
        >
          <span className="font-medium">{orgName}</span>
        </ResourceLink>
      ) : (
        <span className="font-medium">{orgName}</span>
      );
      return fromUserName ? (
        <>
          {fromUserName} put their card on {org}. Your card is no longer charged
          for it.
        </>
      ) : (
        <>The card paying for {org} was changed. Yours is no longer charged.</>
      );
    }

    case NotificationTypeJS.OrgBillingAttention: {
      const orgId = notificationData.org_notif?.org_id as string | undefined;
      const orgName = notificationData.org_notif?.org_name;
      return (
        <>
          {fromUserName} is no longer in{" "}
          {orgId ? (
            <ResourceLink
              resourceType="org"
              resourceId={orgId}
              tooltip={`Open ${orgName} settings`}
            >
              <span className="font-medium">{orgName}</span>
            </ResourceLink>
          ) : (
            <span className="font-medium">{orgName}</span>
          )}
          , but its invoices still go to them. Update the billing email and card
          in Plan &amp; usage.
        </>
      );
    }

    default:
      return <>This notification can no longer be shown.</>;
  }
};
